import { isEqual } from 'lodash';

import { AugmentedUpdateClientPresentationV2Input } from '@/types/schema';

import {
  ClientPresentationShape,
  ClientPresentationV2Configuration,
} from '../ClientPresentationDesigner.types';
import { isSinglePageBundle } from '../components/ClientPresentationDesignerTreeView/ClientPresentationDesignerTreeView.constants';
import { ClientPresentationV2Bundle } from '../types/ClientPresentation.PresentationBundleType';
import {
  PresentationSaveContext,
  PresentationSaveStrategy,
} from './presentationSave.types';
import { PresentationSaveState } from './session/presentationSaveState';

/** Why a save was not attempted. */
export type SkipSaveReason = 'formNotLoaded' | 'matchesServer';

export type PresentationSavePlan =
  | { kind: 'skip'; reason: SkipSaveReason }
  /** A custom slide must have a title before the presentation can be saved. */
  | { kind: 'blocked'; missingHeaderNavId: string }
  | {
      kind: 'write';
      input: AugmentedUpdateClientPresentationV2Input;
      /** Handles post-save synchronization. */
      strategy: PresentationSaveStrategy;
      /** The state sent by this write. */
      sentState: PresentationSaveState;
      /** Bundles that this write asks the server to generate. */
      pendingGenerateBundleIds: string[];
    };

/** Form state as `useWatch` reports it during loading. */
export interface WatchedPresentationState {
  bundles: ClientPresentationV2Bundle[] | undefined;
  presentationConfiguration: ClientPresentationV2Configuration | undefined;
  title: ClientPresentationShape['title'] | undefined;
}

export interface CreatePresentationSavePlanArgs {
  /** What the server holds. Null until the form has loaded. */
  baseline: PresentationSaveState | null;
  current: WatchedPresentationState;
  context: PresentationSaveContext;
  /** The save strategy selected by the feature flag. */
  strategy: PresentationSaveStrategy;
}

/** The first custom page with a blank title, as an id to navigate to. */
function findMissingHeaderNavId(
  bundles: ClientPresentationV2Bundle[]
): string | undefined {
  for (const bundle of bundles) {
    const invalidPage = bundle.pages.find(
      (page) => page.customPage && !page.customPage.pageName?.trim()
    );
    if (invalidPage) {
      // Single-page bundles navigate by bundle.id; multi-page by page.id
      return isSinglePageBundle(bundle.type) ? bundle.id : invalidPage.id;
    }
  }
  return undefined;
}

/** Creates a save plan without causing side effects. */
export function createPresentationSavePlan({
  baseline,
  current,
  context,
  strategy,
}: CreatePresentationSavePlanArgs): PresentationSavePlan {
  if (!baseline) {
    return { kind: 'skip', reason: 'formNotLoaded' };
  }

  // The query can seed the baseline before useWatch reports every form value.
  // Empty bundles and titles are valid, so only undefined means not loaded.
  const { bundles, presentationConfiguration, title } = current;
  if (!bundles || !presentationConfiguration || title === undefined) {
    return { kind: 'skip', reason: 'formNotLoaded' };
  }
  const loaded: PresentationSaveState = {
    bundles,
    presentationConfiguration,
    title,
  };

  // An edit that returns to the server state needs no write.
  if (
    isEqual(bundles, baseline.bundles) &&
    isEqual(presentationConfiguration, baseline.presentationConfiguration) &&
    title === baseline.title
  ) {
    return { kind: 'skip', reason: 'matchesServer' };
  }

  const missingHeaderNavId = findMissingHeaderNavId(bundles);
  if (missingHeaderNavId) {
    return { kind: 'blocked', missingHeaderNavId };
  }

  const buildArgs = {
    bundles,
    presentationConfiguration,
    title,
    context,
    previous: baseline,
  };

  const input = strategy.buildPresentationInput(buildArgs);

  // Unsupported incremental changes are skipped, never escalated to a full save.
  if (!input) {
    return { kind: 'skip', reason: 'matchesServer' };
  }

  return {
    kind: 'write',
    input,
    strategy,
    sentState: loaded,
    pendingGenerateBundleIds: bundles
      .filter((bundle) => bundle.pendingGenerate)
      .map((bundle) => bundle.id),
  };
}
